/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

function collectCartSections() {
  const sections = new Map();
  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try { entries = el.getSectionsToRender?.(); } catch { continue; }
    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;
      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;
      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;
      sections.set(sectionId, { mount, extractSelector: entry.selector || '.shopify-section' });
    }
  }
  return sections;
}

async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size ? `?sections=${[...sections.keys()].join(',')}` : '';
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }
  publish(PUB_SUB_EVENTS.cartUpdate, { source: 'external-refresh', cartData: cartData ?? undefined });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;
  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') { drawer.open(); return; }
      return defaultHandler();
    },
  });
  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try { await refreshDawnCartUI(); }
      catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

if (window.Shopify?.actions) initStandardActions();
else document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });

document.addEventListener('click', (event) => {
  const link = event.target.closest?.('.splity-shared-menu a[href]');
  if (!link) return;
  const href = String(link.getAttribute('href') || '');
  const isBuy = link.hasAttribute('data-shared-buy') || /[?&]mode=buy(?:&|$)/.test(href);
  const isShare = /[?&]mode=share(?:&|$)/.test(href);
  if (!isBuy && !isShare) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const mode = isBuy ? 'buy' : 'share';
  if (isBuy) {
    try {
      sessionStorage.removeItem('splity-buy-intent');
      localStorage.removeItem('splity-buy-intent');
    } catch (_) {}
  }
  const url = new URL('/pages/explorar-servicios', window.location.origin);
  url.searchParams.set('splityCatalog', '1');
  url.searchParams.set('splityMode', mode);
  url.searchParams.set('splityFresh', Date.now().toString());
  document.documentElement.style.overflow = '';
  window.location.assign(url.toString());
}, true);

/* Splity checkout three-tone hotfix — target theme 2026-10-04 */
(() => {
  if (window.__splityThreeToneCheckout) return;
  window.__splityThreeToneCheckout = true;

  const css = `
  html:not([data-splity-theme="light"]) .splity-services:has(.splity-checkout){background:#0a0a0d!important;color:#fff!important}
  html:not([data-splity-theme="light"]) .splity-checkout{color:#fff!important}
  html:not([data-splity-theme="light"]) .splity-checkout :is(.splity-checkout__plan,.splity-checkout__card,.splity-code-box,.splity-access-warning,.splity-contract-summary,.splity-checkout-support-note){background:#14091f!important;background-image:none!important;color:#fff!important;border:1px solid #fff!important;box-shadow:none!important}
  html:not([data-splity-theme="light"]) .splity-checkout :is(h1,h2,h3,h4,p,strong,b,small,label,del,a,span,.splity-rating,.splity-check,.splity-final-price,.splity-saving,.splity-terms,.splity-legal-inline){color:#fff!important;opacity:1!important}
  html:not([data-splity-theme="light"]) .splity-checkout .splity-saving{background:#0a0a0d!important;background-image:none!important;color:#fff!important;border:1px solid #fff!important}
  html:not([data-splity-theme="light"]) .splity-checkout :is(.splity-periods button,.splity-code-box input,.splity-code-box button){background:#0a0a0d!important;background-image:none!important;color:#fff!important;border:1px solid #fff!important;box-shadow:none!important}
  html:not([data-splity-theme="light"]) .splity-checkout :is(.splity-periods button.is-active,.splity-confirm,.splity-confirm--link,[data-confirm]){background:#14091f!important;background-image:none!important;color:#fff!important;border:1px solid #fff!important;box-shadow:none!important}
  html:not([data-splity-theme="light"]) .splity-checkout :is(.splity-confirm:disabled,[data-confirm]:disabled){background:#0a0a0d!important;color:#fff!important;opacity:.55!important}
  html:not([data-splity-theme="light"]) .splity-checkout :is(.splity-terms-error,.splity-purchase-inline-error){background:#14091f!important;color:#fff!important;border-color:#fff!important}
  html:not([data-splity-theme="light"]) .splity-checkout input::placeholder{color:#fff!important;opacity:.65!important}

  html[data-splity-theme="light"] .splity-services:has(.splity-checkout){background:#f6f1ff!important;color:#111!important}
  html[data-splity-theme="light"] .splity-checkout{color:#111!important}
  html[data-splity-theme="light"] .splity-checkout :is(.splity-checkout__plan,.splity-checkout__card,.splity-code-box,.splity-access-warning,.splity-contract-summary,.splity-checkout-support-note){background:#fff!important;background-image:none!important;color:#111!important;border:1px solid #111!important;box-shadow:none!important}
  html[data-splity-theme="light"] .splity-checkout :is(h1,h2,h3,h4,p,strong,b,small,label,del,a,span,.splity-rating,.splity-check,.splity-final-price,.splity-saving,.splity-terms,.splity-legal-inline){color:#111!important;opacity:1!important}
  html[data-splity-theme="light"] .splity-checkout .splity-saving{background:#f6f1ff!important;background-image:none!important;color:#111!important;border:1px solid #111!important}
  html[data-splity-theme="light"] .splity-checkout :is(.splity-periods button,.splity-code-box input,.splity-code-box button){background:#f6f1ff!important;background-image:none!important;color:#111!important;border:1px solid #111!important;box-shadow:none!important}
  html[data-splity-theme="light"] .splity-checkout :is(.splity-periods button.is-active,.splity-confirm,.splity-confirm--link,[data-confirm]){background:#111!important;background-image:none!important;color:#fff!important;border:1px solid #111!important;box-shadow:none!important}
  html[data-splity-theme="light"] .splity-checkout :is(.splity-confirm:disabled,[data-confirm]:disabled){background:#f6f1ff!important;color:#111!important;opacity:.55!important}
  html[data-splity-theme="light"] .splity-checkout :is(.splity-terms-error,.splity-purchase-inline-error){background:#f6f1ff!important;color:#111!important;border-color:#111!important}
  html[data-splity-theme="light"] .splity-checkout input::placeholder{color:#111!important;opacity:.6!important}

  html:not([data-splity-theme="light"]) .splity-support{--ss-ink:#fff!important;--ss-muted:#fff!important;--ss-line:#fff!important;--ss-card:#14091f!important;--ss-bg:#0a0a0d!important;--ss-violet:#fff!important;--ss-purple:#fff!important;--ss-mint:#fff!important}
  html:not([data-splity-theme="light"]) .splity-support :is(.splity-support__launcher,.splity-support__hero,.splity-support__bot-avatar,.splity-support__brand-mark,.splity-support__promise>span,.splity-support__message.is-user,.splity-support__composer button,.splity-support__contact button,.splity-support__collection>i,.splity-support__help-row>span:first-child){background:#14091f!important;background-image:none!important;color:#fff!important;border-color:#fff!important;box-shadow:none!important}
  html:not([data-splity-theme="light"]) .splity-support :is(.splity-support__panel,.splity-support__primary-action,.splity-support__help-card,.splity-support__promise,.splity-support__search,.splity-support__message.is-bot,.splity-support__quick button,.splity-support__composer,.splity-support__article-card,.splity-support__collection,.splity-support__contact input,.splity-support__contact textarea,.splity-support__nav){background:#0a0a0d!important;background-image:none!important;color:#fff!important;border-color:#fff!important;box-shadow:none!important}
  html:not([data-splity-theme="light"]) .splity-support :is(h1,h2,h3,p,b,strong,small,span,label,button,input,textarea,svg){color:#fff!important}
  html[data-splity-theme="light"] .splity-support{--ss-ink:#111!important;--ss-muted:#111!important;--ss-line:#111!important;--ss-card:#fff!important;--ss-bg:#f6f1ff!important;--ss-violet:#111!important;--ss-purple:#111!important;--ss-mint:#111!important}
  html[data-splity-theme="light"] .splity-support :is(.splity-support__launcher,.splity-support__hero,.splity-support__bot-avatar,.splity-support__promise>span,.splity-support__message.is-user,.splity-support__composer button,.splity-support__contact button){background:#111!important;background-image:none!important;color:#fff!important;border-color:#111!important;box-shadow:none!important}
  html[data-splity-theme="light"] .splity-support :is(.splity-support__panel,.splity-support__primary-action,.splity-support__help-card,.splity-support__promise,.splity-support__search,.splity-support__message.is-bot,.splity-support__quick button,.splity-support__composer,.splity-support__article-card,.splity-support__collection,.splity-support__contact input,.splity-support__contact textarea,.splity-support__nav){background:#fff!important;background-image:none!important;color:#111!important;border-color:#111!important;box-shadow:none!important}
  html[data-splity-theme="light"] .splity-support :is(.splity-support__brand-mark,.splity-support__collection>i,.splity-support__help-row>span:first-child){background:#f6f1ff!important;background-image:none!important;color:#111!important;border-color:#111!important}
  `;

  const style = document.createElement('style');
  style.id = 'splity-three-tone-checkout-style';
  style.textContent = css;
  document.head.appendChild(style);

  const removeLegacyAccessNotice = (scope = document) => {
    const cards = [];
    if (scope.matches?.('.splity-checkout .splity-access-warning')) cards.push(scope);
    scope.querySelectorAll?.('.splity-checkout .splity-access-warning').forEach((card) => cards.push(card));
    cards.forEach((card) => {
      const title = String(card.querySelector('strong')?.textContent || '').replace(/\s+/g,' ').trim().toLowerCase();
      if (title.includes('la plataforma puede pedirte un código de acceso')) card.remove();
    });
  };

  const start = () => {
    removeLegacyAccessNotice(document);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element) removeLegacyAccessNotice(node);
        }
      }
    });
    observer.observe(document.body, {childList:true, subtree:true});
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once:true});
  else start();
})();
